import { addDays, differenceInDays, format, isAfter, parseISO } from 'date-fns';
import type { StoreApi } from 'zustand';

import type { User } from '@/api/auth';
import type { Bonus } from '@/api/common';
import { Country } from '@/helpers/conf';
import { amountWithCurrencySymbol, CurrencyCode } from '@/helpers/currency';
import { Locale as ELocale } from '@/helpers/languages';
import { USER_GROUP } from '@/helpers/user';
import { STORAGE, useLocalStorageStore } from '@/store';
import type { CommonStoreType } from '@/store/commonStore/types';

export const BONANZA_PROMO_ALLOWED_COUNTRIES: string[] = [Country.Australia];

export const BONANZA_PROMO_RESTRICTED_GROUPS = [
  USER_GROUP.BA,
  USER_GROUP.BR,
  USER_GROUP.VIPBA,
  USER_GROUP.RISK_SEGMENT_S1,
  USER_GROUP.RISK_SEGMENT_S2,
  USER_GROUP.RISK_SEGMENT_S3,
];

export enum BonanzaPromoStatus {
  LEVEL_0 = '0',
  LEVEL_1 = '25',
  LEVEL_2 = '50',
  LEVEL_3 = '75',
  LEVEL_4 = '100', // user recieves bonus when he has Bonanza_100% group
  // tech status, not a user group
  UNLOGGED = 'Unlogged',
  PROMO_LOCKED = 'PROMO_LOCKED',
}

const BONANZA_PROMO_ID = 'Reward';
export const BONANZA_ITERATION_DURATION_DAYS = 3;

export const getUserBonanzaPromoGroup = (user?: User) => {
  if (!user) return BonanzaPromoStatus.UNLOGGED;

  const bonusGroupId = findBonanzaPromoGroup(user);
  if (!bonusGroupId) return BonanzaPromoStatus.LEVEL_0;

  const bonusGroupPercent = bonusGroupId.split('_').slice(-2)[0];

  const isLevel4 = bonusGroupPercent === BonanzaPromoStatus.LEVEL_4;

  const isBonusBonanzaCLickStorage = useLocalStorageStore
    .getState()
    .get(STORAGE.BONUS_BONANZA_CLICK);

  if (isLevel4 && isBonusBonanzaCLickStorage)
    return BonanzaPromoStatus.PROMO_LOCKED;

  if (!isLevel4 && isBonusBonanzaCLickStorage) {
    useLocalStorageStore.getState().remove(STORAGE.BONUS_BONANZA_CLICK);
  }

  return bonusGroupPercent as BonanzaPromoStatus;
};

export const userRewardCurrencyList: string[] = [
  CurrencyCode.AUD,
  CurrencyCode.EUR,
  CurrencyCode.USD,
];

type CurrencyRewardMap = Record<string, number>;
export const rewardMapPerCurrency: Record<string, CurrencyRewardMap> = {
  [USER_GROUP.Bonanza_Group_A]: {
    [CurrencyCode.AUD]: 8,
    [CurrencyCode.EUR]: 5,
    [CurrencyCode.USD]: 5,
  },
  [USER_GROUP.Bonanza_Group_B]: {
    [CurrencyCode.AUD]: 15,
    [CurrencyCode.EUR]: 8,
    [CurrencyCode.USD]: 8,
  },
  [USER_GROUP.Bonanza_Group_C]: {
    [CurrencyCode.AUD]: 25,
    [CurrencyCode.EUR]: 15,
    [CurrencyCode.USD]: 15,
  },
  [USER_GROUP.Bonanza_Group_D]: {
    [CurrencyCode.AUD]: 45,
    [CurrencyCode.EUR]: 30,
    [CurrencyCode.USD]: 30,
  },
  [USER_GROUP.Bonanza_Group_E]: {
    [CurrencyCode.AUD]: 145,
    [CurrencyCode.EUR]: 100,
    [CurrencyCode.USD]: 100,
  },
};

export const bonanzaPromoProgressPercent = {
  [BonanzaPromoStatus.LEVEL_0]: 0,
  [BonanzaPromoStatus.LEVEL_1]: 25,
  [BonanzaPromoStatus.LEVEL_2]: 50,
  [BonanzaPromoStatus.LEVEL_3]: 75,
  [BonanzaPromoStatus.LEVEL_4]: 100,
  [BonanzaPromoStatus.UNLOGGED]: 0,
  [BonanzaPromoStatus.PROMO_LOCKED]: 0,
};

const findBonanzaPromoGroup = (user?: User) => {
  if (!user?.statuses) return;

  const regexPattern = /^group_([a-h])_\d+_mar26$/;
  const bonanzaRegex = new RegExp(regexPattern, 'i');

  const matches = user.statuses.filter(({ id }) => bonanzaRegex.test(id));

  if (!matches.length) return;

  return matches
    .reduce((highest, current) => {
      const highestPercent = Number.parseInt(
        highest.id.split('_').slice(-2)[0],
        10
      );
      const currentPercent = Number.parseInt(
        current.id.split('_').slice(-2)[0],
        10
      );
      return currentPercent > highestPercent ? current : highest;
    })
    .id.toLowerCase();
};

const findBonanzaRewardGroup = (user?: User) => {
  if (!user?.statuses) return;

  const rewardGroupRegex = /^Group_([A-Ha-h])$/;
  const match = user.statuses.find(({ id }) => rewardGroupRegex.test(id));

  return match?.id.toLowerCase();
};

export const getUserReward = (_: string, user?: User) => {
  const currency = user?.currency || CurrencyCode.AUD;

  const promoCurrency = userRewardCurrencyList.includes(currency)
    ? currency
    : CurrencyCode.AUD;

  const userGroupName =
    findBonanzaRewardGroup(user) ?? USER_GROUP.Bonanza_Group_A;

  const reward = rewardMapPerCurrency[userGroupName]?.[promoCurrency] ?? 0;

  return { reward, currency: promoCurrency };
};

export const getBonanzaPromoBonus = (bonuses: Bonus[]) =>
  bonuses.find(({ title }) => title?.includes(BONANZA_PROMO_ID));

export const updateBonanzaData = (
  setState: StoreApi<CommonStoreType>['setState'],
  language: string,
  user?: User
) => {
  const userBonanzaGroup = getUserBonanzaPromoGroup(user);

  const bonanzaProgressPercent = bonanzaPromoProgressPercent[userBonanzaGroup];

  const userReward = getUserReward(language, user);

  const userBonanzaGroupKey = Object.entries(BonanzaPromoStatus).find(
    ([, value]) => value === userBonanzaGroup
  )?.[0];

  setState({
    userBonanzaGroup,
    userBonanzaGroupKey,
    bonanzaProgressPercent,
    bonanzaReward: amountWithCurrencySymbol(
      userReward.currency,
      userReward.reward
    ),
  });
};

export const checkIsBonanzaPromoLastRound = (endDate?: string) => {
  if (!endDate) return false;

  const today = new Date();
  const nextIterationStart = addDays(today, BONANZA_ITERATION_DURATION_DAYS);
  const isLastRound = isAfter(nextIterationStart, new Date(endDate));

  return isLastRound;
};

export const getCurrentBonanzaIterationDays = (
  startDate: string,
  locale: Locale
): {
  startDay: string;
  endDay: string;
  endDayShort: string;
  nextStartDay: string;
  nextStartDayFull: string;
} => {
  const promoStart = parseISO(startDate);
  const today = new Date();
  const daysSinceStart = differenceInDays(today, promoStart);

  const currentIteration = Math.max(
    0,
    Math.floor(daysSinceStart / BONANZA_ITERATION_DURATION_DAYS)
  );

  const currentIterationStart = addDays(
    promoStart,
    currentIteration * BONANZA_ITERATION_DURATION_DAYS
  );
  const currentIterationEnd = addDays(
    currentIterationStart,
    BONANZA_ITERATION_DURATION_DAYS - 1
  );

  const nextIterationStart = addDays(
    currentIterationStart,
    BONANZA_ITERATION_DURATION_DAYS
  );

  return {
    startDay: format(currentIterationStart, 'EEE', { locale }),
    endDay: format(currentIterationEnd, 'EEE', { locale }),
    endDayShort: format(currentIterationEnd, 'EEE', { locale }),
    nextStartDay: format(nextIterationStart, 'EEE', { locale }),
    nextStartDayFull: format(nextIterationStart, 'EEEE', { locale }),
  };
};

export const BONANZA_LABEL_TEXT: Partial<Record<BonanzaPromoStatus, string>> = {
  [BonanzaPromoStatus.UNLOGGED]: 'label.unlogged',
  [BonanzaPromoStatus.PROMO_LOCKED]: 'label.locked',
};

export const END_DATE_FORMAT_MAP: Record<string, string> = {
  [ELocale.GERMAN]: 'd. MMM',
  [ELocale.FRENCH_CANADA]: 'd MMM',
  [ELocale.FRENCH_SWITZERLAND]: 'd MMM',
};
